import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchVerificationLogs, verifyOrder, type VerificationLogsParams, type VerificationPayload } from '@/api/verification'

export function useVerificationLogs(isVerificationCenter: ComputedRef<boolean>) {
  const loadingVerificationLogs = ref(false)
  const verificationVisible = ref(false)
  const verificationSubmitting = ref(false)
  const verificationLogPage = ref<Awaited<ReturnType<typeof fetchVerificationLogs>>>()
  const verificationFilters = reactive<VerificationLogsParams>({ page: 1, pageSize: 10, code: '', orderNo: '', result: '' })
  const verificationForm = reactive<VerificationPayload>({
    verifyCode: '',
    orderId: '',
  })

  const verificationLogTotalPages = computed(() => !verificationLogPage.value ? 1 : Math.max(1, Math.ceil(verificationLogPage.value.total / verificationLogPage.value.pageSize)))
  const verificationStats = computed(() => {
    const list = verificationLogPage.value?.list ?? []
    return [
      { label: '核销记录', value: String(verificationLogPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '成功核销', value: String(list.filter((item) => item.result === 'SUCCESS').length), sub: '当前页统计', tone: 'success' as const },
      { label: '失败记录', value: String(list.filter((item) => item.result === 'FAIL').length), sub: '当前页统计', tone: 'error' as const },
      { label: '重复核销', value: String(list.filter((item) => item.result === 'DUPLICATE').length), sub: '需要复核', tone: 'warning' as const },
    ]
  })

  async function loadVerificationLogs(): Promise<void> {
    if (!isVerificationCenter.value) return
    loadingVerificationLogs.value = true
    try {
      verificationLogPage.value = await fetchVerificationLogs({
        page: verificationFilters.page,
        pageSize: verificationFilters.pageSize,
        code: verificationFilters.code || undefined,
        orderNo: verificationFilters.orderNo || undefined,
        result: verificationFilters.result || undefined,
      })
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '核销记录加载失败')
    } finally {
      loadingVerificationLogs.value = false
    }
  }

  async function searchVerificationLogs(): Promise<void> {
    verificationFilters.page = 1
    await loadVerificationLogs()
  }

  async function resetVerificationFilters(): Promise<void> {
    verificationFilters.code = ''
    verificationFilters.orderNo = ''
    verificationFilters.result = ''
    verificationFilters.page = 1
    await loadVerificationLogs()
  }

  async function changeVerificationPage(page: number): Promise<void> {
    verificationFilters.page = page
    await loadVerificationLogs()
  }

  function openVerificationDialog(): void {
    verificationForm.verifyCode = ''
    verificationForm.orderId = ''
    verificationVisible.value = true
  }

  async function submitVerification(): Promise<void> {
    const verifyCode = verificationForm.verifyCode.trim()
    if (!verifyCode) {
      ElMessage.warning('请输入核销码')
      return
    }
    verificationSubmitting.value = true
    try {
      await verifyOrder({
        verifyCode,
        orderId: verificationForm.orderId?.trim() || undefined,
      })
      ElMessage.success('核销已提交')
      verificationVisible.value = false
      await loadVerificationLogs()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '执行核销失败')
    } finally {
      verificationSubmitting.value = false
    }
  }

  return {
    loadingVerificationLogs,
    verificationVisible,
    verificationSubmitting,
    verificationLogPage,
    verificationFilters,
    verificationForm,
    verificationLogTotalPages,
    verificationStats,
    loadVerificationLogs,
    searchVerificationLogs,
    resetVerificationFilters,
    changeVerificationPage,
    openVerificationDialog,
    submitVerification,
  }
}
